import { useEffect, useState } from 'react';
import { namedChainsApi } from './api/named-chains-client';
import '../admin.css';

export interface NamedChainCreateModalProps {
  onClose: () => void;
  onCreated: (id: string) => void;
}

/**
 * In-page create modal for Named Chains (F-001).
 *
 * Reuses the shared admin dialog pattern (`dialog-overlay` / `dialog-panel`
 * / `dialog-field` / `rb-input` / `rb-footer`) — the same chrome as
 * CreateJobTypeDialog and the Lookup Tables create dialog — so Named Chain
 * creation matches the rest of admin instead of routing to a bespoke page.
 */
export function NamedChainCreateModal({ onClose, onCreated }: NamedChainCreateModalProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const created = await namedChainsApi.create({ name: name.trim() });
      onCreated(created.id);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <form
        className="dialog-panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className="dialog-title">New Named Chain</h2>
        <div className="dialog-field">
          <label className="dialog-label" htmlFor="nc-name">
            Name
          </label>
          <input
            id="nc-name"
            className="rb-input"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (err) setErr(null);
            }}
            placeholder="e.g. Uppercase + Trim"
            autoFocus
          />
          {err && <span className="dialog-field-error">{err}</span>}
        </div>
        <div className="rb-footer">
          <button
            type="button"
            className="rb-btn rb-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rb-btn rb-btn-save"
            disabled={!name.trim() || saving}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
